import HCLTreeVertex from '../HCLTreeVertex';
import BayesianNetworkVertexValue from './BayesianNetworkVertexValue';
import ReferenceTypes from "../HCLTreeVertexValue/ReferenceTypes";
import {mxCell, mxConstants, mxCylinder, mxGeometry, mxUtils} from "mxgraph-js";
import Outcome from "../HCLTreeVertexValue/Outcome";
import ProxyTypes from "../HCLTreeVertexValue/ProxyTypes";

class BayesianNetworkVertex extends HCLTreeVertex {
  static SHAPE_KEY = 'bbn-vertex';

  /**
   * Basic shape parameters, used and optionally overridden by child classes
   * @type {{RADIUS: number, WIDTH: number, HEIGHT: number, GATE: number}}
   */
  static SHAPE = {
    HEIGHT: 100,
    WIDTH: 100,
    RADIUS: 10,
    GATE: 20,
  };

  /**
   * @return {() => void}
   */
  static getShape(): () => void {
    function BayesianNetworkVertexShape() { mxCylinder.call(this); }
    mxUtils.extend(BayesianNetworkVertexShape, mxCylinder);
    BayesianNetworkVertexShape.prototype.redrawPath = (c: any, x: any, y: any, w: any, h: any) => {
      const gateSize = BayesianNetworkVertex.SHAPE.GATE;
      const halfSize = BayesianNetworkVertex.SHAPE.GATE / 2;
      const radius = BayesianNetworkVertex.SHAPE.RADIUS;
      c.roundrect(0, 0, w, h - gateSize, radius, radius);
      c.close();
      c.fillAndStroke();
      c.ellipse(w * 0.5 - halfSize, h - gateSize + 2, gateSize, gateSize);
    };
    return BayesianNetworkVertexShape;
  }

  /**
   * @param {string} key
   * @param {BayesianNetworkVertexValue} value
   */
  constructor(key: string, value: BayesianNetworkVertexValue) {
    super(key, value);
  }

  getType(): string {
    return 'Bayesian Network';
  }

  /**
   * @override
   * @return {MxGraphType.mxGeometry}
   */
  getMyGeometry(): mxGeometry {
    return new mxGeometry(0, 0, BayesianNetworkVertex.SHAPE.WIDTH, BayesianNetworkVertex.SHAPE.HEIGHT);
  }

  /**
   * @override
   * @return {[index: string]: any}
   */
  getMyStyle(): {[index: string]: any} {
    const style: { [index: string]: any } = {};
    style[mxConstants.STYLE_ROUNDED] = 1;
    style[mxConstants.STYLE_SPACING] = 6;
    style[mxConstants.STYLE_FILLCOLOR] = 'rgba(182,182,185,0.65)';
    style[mxConstants.STYLE_STROKECOLOR] = 'rgba(131,135,151,0.65)';
    style[mxConstants.STYLE_FILLCOLOR] = 'rgba(193,187,227,0.20)';
    style[mxConstants.STYLE_STROKECOLOR] = 'rgba(71,76,151,0.75)';
    style[mxConstants.STYLE_FILL_OPACITY] = '50';
    style[mxConstants.STYLE_STROKE_OPACITY] = '100';
    style[mxConstants.STYLE_STROKEWIDTH] = '2';
    style[mxConstants.STYLE_FONTCOLOR] = 'inherit';
    style[mxConstants.STYLE_FONTSIZE] = '12';
    style[mxConstants.STYLE_FONTFAMILY] = 'Roboto Condensed';
    // style[MxGraph.mxConstants.STYLE_SHAPE] = 'ellipse';
    // style[MxGraph.mxConstants.STYLE_PERIMETER] = MxGraph.mxPerimeter.EllipsePerimeter;
    // style[MxGraph.mxConstants.STYLE_SPACING_BOTTOM] = BayesianNetworkVertex.SHAPE.GATE;
    style[mxConstants.STYLE_WHITE_SPACE] = 'wrap';
    style[mxConstants.STYLE_AUTOSIZE] = 1;
    style[mxConstants.STYLE_RESIZABLE] = 0;
    style[mxConstants.STYLE_ASPECT] = 'fixed';
    style[mxConstants.STYLE_OVERFLOW] = 'hidden';
    // style[MxGraph.mxConstants.STYLE_PERIMETER] = MxGraph.mxPerimeter.EllipsePerimeter;
    style[mxConstants.STYLE_SHAPE] = mxConstants.SHAPE_ELLIPSE;
    return style;
  }

  /**
   * @return {ReferenceTypes}
   */
  public getReferenceType(): ReferenceTypes {
    return ReferenceTypes.BAYESIAN_NODES;
  }

  clone(): BayesianNetworkVertex {
    return new BayesianNetworkVertex(null, this.value.clone());
  }

  public onRequestConnectChildVertex(vertex: HCLTreeVertex): mxCell {
    return this.getGraph().addEdgeBetween(this, vertex);
  }

  toReferenceJSON() {
    /**
     * TODO: Check how to handle tree ID
     * TODO: Check how to handle path
     */
    return new Outcome(this.getKey(), this.getReferenceType(), this.getGraph().getModelTreeId(), '', ProxyTypes.EVENT_REFERENCE).toJSON();
  }
}

export default BayesianNetworkVertex;
